import ClearIcon from '@mui/icons-material/Clear';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  TextField,
} from '@mui/material';
import {Box} from '@mui/system';
import {camelCase, startCase, upperFirst} from 'lodash';
import {useRef, useState} from 'react';

import {DataGridColumn, DataGridRow, DataGridSortBy, DataGridSortDir} from '.';

interface Props<T extends DataGridRow> {
  column: DataGridColumn<T>;
  sortBy?: DataGridSortBy | null;
  columnFilters?: Record<string, string>;
  shouldFocusFilter?: boolean;
  externalAnchorEl?: HTMLElement | null;
  onSort?: (dir: DataGridSortDir) => void;
  onColumnFiltersChange?: (filters: Record<string, string>) => void;
  onMenuClose?: () => void;
  onArrangeColumnsClick?: () => void;
}

const titleCase = (value: string) =>
  upperFirst(startCase(camelCase(value)).toLowerCase());

export const DataGridColumnActionsButton = <T extends DataGridRow>({
  column,
  sortBy,
  columnFilters,
  shouldFocusFilter,
  externalAnchorEl,
  onSort,
  onColumnFiltersChange,
  onMenuClose,
  onArrangeColumnsClick,
}: Props<T>) => {
  const [internalAnchorEl, setInternalAnchorEl] = useState<HTMLElement | null>(
    null
  );
  const filterInputRef = useRef<HTMLInputElement>(null);
  const firstAnchorEl = externalAnchorEl ?? internalAnchorEl;
  const firstOpen = Boolean(firstAnchorEl);

  const handleMenuClose = () => {
    setInternalAnchorEl(null);
    onMenuClose?.();
  };

  return (
    <Box>
      <IconButton
        size="small"
        sx={{p: 0.5}}
        onClick={(event) => setInternalAnchorEl(event.currentTarget)}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>

      <Menu
        anchorEl={firstAnchorEl}
        open={firstOpen}
        onClose={handleMenuClose}
      >
        {column.filterable ? (
          <Box px={2} py={1}>
            <TextField
              inputRef={filterInputRef}
              autoFocus={shouldFocusFilter}
              size="small"
              fullWidth
              placeholder={
                column.filterPlaceholder ??
                `Filter by ${titleCase(column.field)}...`
              }
              value={columnFilters?.[column.field] ?? ''}
              onChange={(e) => {
                const newFilters = {
                  ...columnFilters,
                  [column.field]: e.target.value,
                };
                onColumnFiltersChange?.(newFilters);
              }}
              InputProps={{
                endAdornment: columnFilters?.[column.field] ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => {
                        const newFilters = {...columnFilters};
                        delete newFilters[column.field];
                        onColumnFiltersChange?.(newFilters);
                        handleMenuClose();
                      }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleMenuClose();
                } else {
                  e.stopPropagation();
                }
              }}
            />
          </Box>
        ) : null}

        {column.sortable ? (
          <Box>
            <MenuItem
              disabled={sortBy?.field === column.field && sortBy?.dir === 'asc'}
              onClick={() => {
                onSort?.('asc');
                setInternalAnchorEl(null);
                onMenuClose?.();
              }}
            >
              Sort Ascending
            </MenuItem>

            <MenuItem
              disabled={
                sortBy?.field === column.field && sortBy?.dir === 'desc'
              }
              onClick={() => {
                onSort?.('desc');
                setInternalAnchorEl(null);
                onMenuClose?.();
              }}
            >
              Sort Descending
            </MenuItem>
          </Box>
        ) : null}

        {/* <MenuItem onClick={(event) => setSecondAnchorEl(event.currentTarget)}>
          Columns
        </MenuItem> */}

        <MenuItem
          onClick={() => {
            handleMenuClose();
            onArrangeColumnsClick?.();
          }}
        >
          Customize Columns
        </MenuItem>

        {/* <Menu
          anchorEl={secondAnchorEl}
          open={secondOpen}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          onClose={() => {
            setSecondAnchorEl(null);
            setInternalAnchorEl(null);
            onMenuClose?.();
          }}
        >
          <Box px={2}>
            {configurableColumns.map((column) => (
              <Box key={column.field}>
                <FormControlLabel
                  label={column.headerName ?? titleCase(column.field)}
                  disabled={column.hideable === false}
                  sx={{display: 'block'}}
                  control={
                    <Checkbox
                      checked={shownFields.includes(column.field)}
                      size="small"
                      onChange={(_event, value) => {
                        const newShownFields = value
                          ? [...shownFields, column.field]
                          : shownFields.filter((i) => i !== column.field);

                        if (newShownFields.length) {
                          onShownFieldsChange?.(newShownFields);
                        } else {
                          onShownFieldsChange?.(columns.map((i) => i.field));
                        }
                      }}
                    />
                  }
                />
              </Box>
            ))}
          </Box>
        </Menu> */}
      </Menu>
    </Box>
  );
};
